/**
 * useFormValidation Hook
 * Manages form validation strategy and error tracking
 */

import { useCallback, useMemo, useState } from 'react';
import { FormContainerConfig, IValidationStrategy } from '../types/index';
import { ValidationStrategyFactory } from '../strategies/ValidationStrategies';
import { useChildFormRegistryStore, childFormKey } from '../stores/ChildFormRegistryStore';

export interface UseFormValidationReturn {
  strategy: IValidationStrategy;
  errors: Record<string, any[]>;
  validateAll: () => Promise<boolean>;
  clearErrors: () => void;
  hasErrors: boolean;
}

/**
 * Hook for managing form validation
 * Handles:
 * - Creating validation strategy from config
 * - Tracking validation errors
 * - Validating all forms
 */
export function useFormValidation(
  config: FormContainerConfig
): UseFormValidationReturn {
  const [errors, setErrors] = useState<Record<string, any[]>>({});

  // Create validation strategy based on config
  const scope = config.registryScope ?? '';
  const strategy = useMemo(
    () => ValidationStrategyFactory.createStrategy(config.validationMode, scope),
    [config.validationMode, scope]
  );

  // Validate all forms
  // Returns true when every child form is valid. Child forms are checked through their
  // registered engines (see validateChildForm), keyed by the configured child form IDs.
  const validateAll = useCallback(async (): Promise<boolean> => {
    const childFormIDs = Object.fromEntries((config.childForms ?? []).map((child) => [child.formID, {}]));
    const validationErrors = await strategy.validateBeforeSave(childFormIDs);

    if (validationErrors && validationErrors.length > 0) {
      // Extract per-form errors (format: { formID: [errors] })
      const errorsByForm: Record<string, any[]> = {};

      validationErrors.forEach((error: any) => {
        if (error.formID) {
          if (!errorsByForm[error.formID]) {
            errorsByForm[error.formID] = [];
          }
          errorsByForm[error.formID].push(error);
        }
      });

      setErrors(errorsByForm);
      // Ask each failing child form to show its own field errors
      Object.keys(errorsByForm).forEach((formID) =>
        useChildFormRegistryStore.getState().bumpValidation(childFormKey(scope, formID))
      );
      strategy.onValidationError(validationErrors);
      return false;
    }

    setErrors({});
    return true;
  }, [config.childForms, strategy, scope]);

  // Clear errors
  const clearErrors = useCallback(() => {
    setErrors({});
  }, []);

  // Check if there are any errors
  const hasErrors = Object.values(errors).some((formErrors) => formErrors.length > 0);

  return {
    strategy,
    errors,
    validateAll,
    clearErrors,
    hasErrors,
  };
}
